import React, { useEffect, useState, useCallback } from 'react';
import { ExecutionPipelinesApiClient } from '../api/client';
import { Icons } from './Icons';

/**
 * Generic entity search screen component
 * Reusable pattern for searching any entity type (Templates, Pipelines, etc.)
 * Configurable via EntitySearchScreenConfig interface
 */

export interface RowAction<T> {
  label: string;
  icon?: string;
  onClick: (entity: T) => void;
  isDangerous?: boolean;
}

export interface ColumnDef<T> {
  key: keyof T;
  label: string;
  width?: string;
  render?: (value: any, entity: T) => React.ReactNode;
}

export interface EntitySearchScreenConfig<T> {
  entityLabel: string;
  entityLabelPlural?: string;
  searchEndpoint: (query: string) => Promise<T[]>;
  storageKey: string;
  columns: ColumnDef<T>[];
  rowActions: RowAction<T>[];
  onAddClick?: () => void;
  showAddButton?: boolean;
}

export interface EntitySearchScreenProps<T> {
  config: EntitySearchScreenConfig<T>;
}

export const EntitySearchScreen = React.forwardRef<any, EntitySearchScreenProps<any>>(
  ({ config }, ref) => {
    const [entities, setEntities] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [enabledFilter, setEnabledFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
    const [scopeFilter, setScopeFilter] = useState<string>('all');

    // Load search state from localStorage
    useEffect(() => {
      try {
        const saved = localStorage.getItem(config.storageKey);
        if (saved) {
          const state = JSON.parse(saved);
          setQuery(state.query || '');
          if (state.sortColumn) setSortColumn(state.sortColumn);
          if (state.sortDirection) setSortDirection(state.sortDirection);
        }
      } catch (err) {
        console.warn('Failed to restore search state:', err);
      }
    }, [config.storageKey]);

    // Fetch entities
    const fetchEntities = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await config.searchEndpoint(query);
        setEntities(Array.isArray(result) ? result : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch entities');
      } finally {
        setLoading(false);
      }
    }, [config, query]);

    // Fetch on mount and when query changes
    useEffect(() => {
      fetchEntities();
    }, [fetchEntities]);

    // Save search state to localStorage
    const saveSearchState = useCallback(() => {
      try {
        localStorage.setItem(
          config.storageKey,
          JSON.stringify({ query, sortColumn, sortDirection })
        );
      } catch (err) {
        console.warn('Failed to save search state:', err);
      }
    }, [config.storageKey, query, sortColumn, sortDirection]);

    useEffect(() => {
      saveSearchState();
    }, [saveSearchState]);

    const handleSort = (column: string) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    };

    const filteredEntities = entities.filter((entity) => {
      // Text search
      if (query) {
        const searchLower = query.toLowerCase();
        const matchesSearch =
          String(entity.name || '').toLowerCase().includes(searchLower) ||
          String(entity.description || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Enabled/Disabled filter
      if (enabledFilter !== 'all') {
        const isEnabled = entity.enabled === true;
        if (enabledFilter === 'enabled' && !isEnabled) return false;
        if (enabledFilter === 'disabled' && isEnabled) return false;
      }

      // Scope filter (for templates)
      if (scopeFilter !== 'all' && entity.executionScope) {
        if (entity.executionScope !== scopeFilter) return false;
      }

      return true;
    });

    const sortedEntities = [...filteredEntities].sort((a, b) => {
      if (!sortColumn) return 0;

      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    const labelPlural = config.entityLabelPlural || `${config.entityLabel}s`;

    return (
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Execution {labelPlural}</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={fetchEntities} style={styles.refreshButton} disabled={loading} title="Refresh">
              <Icons.Refresh />
            </button>
            {config.showAddButton && (
              <button onClick={config.onAddClick} style={styles.addButton} title={`Add ${config.entityLabel}`}>
                <Icons.Plus />
                Add {config.entityLabel}
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {/* Search Box */}
        <div style={styles.searchSection}>
          <Icons.Search />
          <input
            type="text"
            placeholder={`Search ${labelPlural.toLowerCase()} by name...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* Filters Row */}
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Status:</label>
            <div style={styles.filterOptions}>
              {(['all', 'enabled', 'disabled'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setEnabledFilter(opt)}
                  style={{
                    ...styles.filterOption,
                    ...(enabledFilter === opt ? styles.filterOptionActive : {}),
                  }}
                >
                  {opt === 'all' && 'All'}
                  {opt === 'enabled' && (
                    <>
                      <Icons.CheckCircle /> Enabled
                    </>
                  )}
                  {opt === 'disabled' && (
                    <>
                      <Icons.XCircle /> Disabled
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Scope Filter (for Templates) */}
          {entities.some((e) => e.executionScope) && (
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Scope:</label>
              <div style={styles.filterOptions}>
                {['all', 'Process', 'ProcessThread', 'ProcessElement'].map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setScopeFilter(opt)}
                    style={{
                      ...styles.filterOption,
                      ...(scopeFilter === opt ? styles.filterOptionActive : {}),
                    }}
                  >
                    {opt === 'all' ? 'All' : opt}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Table or Loading/Empty */}
        {loading && <div style={styles.loadingContainer}>Loading {labelPlural.toLowerCase()}...</div>}

        {!loading && sortedEntities.length === 0 && (
          <div style={styles.emptyContainer}>
            No {labelPlural.toLowerCase()} found{query && ` matching "${query}"`}
          </div>
        )}

        {!loading && sortedEntities.length > 0 && (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  {config.columns.map((col) => (
                    <th
                      key={String(col.key)}
                      onClick={() => handleSort(String(col.key))}
                      style={{
                        ...styles.headerCell,
                        cursor: 'pointer',
                        fontWeight: sortColumn === String(col.key) ? 'bold' : 'normal',
                        width: col.width,
                      }}
                    >
                      {col.label}{' '}
                      {sortColumn === String(col.key) && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  ))}
                  {config.rowActions.length > 0 && <th style={styles.headerCell}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedEntities.map((entity, idx) => (
                  <tr key={idx} style={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                    {config.columns.map((col) => (
                      <td key={String(col.key)} style={styles.cell}>
                        {col.render ? col.render(entity[col.key], entity) : String(entity[col.key] ?? '—')}
                      </td>
                    ))}
                    {config.rowActions.length > 0 && (
                      <td style={{ ...styles.cell, display: 'flex', gap: '8px' }}>
                        {config.rowActions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => action.onClick(entity)}
                            style={{
                              ...styles.actionButton,
                              ...(action.isDangerous && styles.dangerActionButton),
                            }}
                            title={action.label}
                          >
                            {action.label}
                          </button>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
);

EntitySearchScreen.displayName = 'EntitySearchScreen';

const styles = {
  container: {
    padding: '16px',
  },
  header: {
    display: 'flex' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--text-primary, #e5e7eb)',
  },
  refreshButton: {
    padding: '8px 12px',
    backgroundColor: 'var(--primary-color, #30a046)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  addButton: {
    padding: '8px 14px',
    backgroundColor: 'var(--primary-color, #30a046)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  errorBanner: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: 'var(--error-bg, rgba(211, 47, 47, 0.15))',
    color: 'var(--error-color, #d32f2f)',
    borderRadius: '4px',
    fontSize: '13px',
    border: '1px solid var(--error-color, #d32f2f)',
  },
  searchSection: {
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid var(--border-color, rgba(255, 255, 255, 0.12))',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-primary, rgb(2, 6, 23))',
    color: 'var(--text-primary, #e5e7eb)',
    minHeight: '36px',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.2s ease',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    border: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    borderRadius: '4px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
    backgroundColor: 'var(--bg-primary, rgb(2, 6, 23))',
  },
  headerRow: {
    backgroundColor: 'var(--bg-secondary, rgb(15, 23, 42))',
    borderBottom: '2px solid var(--border-color, rgba(255, 255, 255, 0.06))',
  },
  headerCell: {
    padding: '10px',
    textAlign: 'left' as const,
    fontWeight: '600' as any,
    borderRight: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    color: 'var(--text-primary, #e5e7eb)',
  },
  rowEven: {
    backgroundColor: 'var(--bg-primary, rgb(2, 6, 23))',
  },
  rowOdd: {
    backgroundColor: 'var(--bg-secondary, rgb(15, 23, 42))',
  },
  cell: {
    padding: '10px',
    borderRight: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    borderBottom: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    color: 'var(--text-primary, #e5e7eb)',
  },
  actionButton: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: 'var(--primary-color, #30a046)',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  dangerActionButton: {
    backgroundColor: 'var(--error-color, #d32f2f)',
  },
  loadingContainer: {
    padding: '32px',
    textAlign: 'center' as const,
    color: 'var(--text-secondary, #9ca3af)',
  },
  emptyContainer: {
    padding: '32px',
    textAlign: 'center' as const,
    color: 'var(--text-secondary, #9ca3af)',
  },
  filtersRow: {
    marginTop: '16px',
    marginBottom: '16px',
    padding: '12px 0',
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap' as const,
    alignItems: 'flex-start',
  },
  filterGroup: {
    marginBottom: '12px',
  },
  filterLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600' as any,
    color: 'var(--text-primary, #e5e7eb)',
    marginBottom: '8px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  filterOptions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
  },
  filterOption: {
    padding: '6px 12px',
    backgroundColor: 'var(--bg-primary, rgb(2, 6, 23))',
    color: 'var(--text-primary, #e5e7eb)',
    border: '1px solid var(--border-color, rgba(255, 255, 255, 0.06))',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  filterOptionActive: {
    backgroundColor: 'var(--primary-color, #30a046)',
    borderColor: 'var(--primary-color, #30a046)',
    color: 'white',
  },
} as const;
